// Pure helpers for Insights dashboard tabs, kept out of the components so they can be
// unit-tested without react-grid-layout.
import { COLS } from './layoutUtils';

export const TAB_STATE = {
  PRIVATE: 'private',
  SHARED_BY_YOU: 'shared_by_you',
  SHARED_WITH_YOU: 'shared_with_you',
};

// One icon per state, same size and colour for all three; the meaning is carried by
// the shape and by the label, never by colour alone.
export const TAB_STATE_ICON = {
  [TAB_STATE.PRIVATE]: { icon: 'lock', label: 'Private' },
  [TAB_STATE.SHARED_BY_YOU]: { icon: 'group', label: 'Shared with your organization' },
  [TAB_STATE.SHARED_WITH_YOU]: { icon: 'visibility', label: 'Shared with you' },
};

export const MAX_TAB_NAME_LENGTH = 40;
export const MAX_TABS = 20;

// The tab bar: everything the user has not hidden, in their order (the server's).
export function visibleTabs(tabs) {
  return (tabs || []).filter((t) => !t.hidden);
}

export function ownTabs(tabs) {
  return (tabs || []).filter((t) => t.is_own);
}

// Where widgets nobody has placed on a tab you can see go: your first own tab. Not
// the first tab in the bar -- that may be someone else's shared tab, which is
// read-only and arranged by its owner.
export function homeTab(tabs) {
  return ownTabs(tabs)[0] || null;
}

// The tab to show for a requested id: that tab if it is in the bar, otherwise the
// first one. An unknown, deleted or hidden id is not an error, just a fallback.
export function resolveActiveTab(tabs, requestedId) {
  const shown = visibleTabs(tabs);
  return shown.find((t) => t.id === requestedId) || shown[0] || null;
}

// The widgets drawn on one tab.
//
// A widget sits on its own tab_id when that tab is one the user sees. Anything else
// readable -- a colleague's widget shared on its own, or every colleague widget while
// org sharing is off and the whole org is readable -- has no tab here, and lands on
// the home tab, below the user's own arrangement.
export function widgetsForTab(widgets, tabs, tabId) {
  if (!tabId) return [];
  const known = new Set((tabs || []).map((t) => t.id));
  const home = homeTab(tabs);
  const onTab = [];
  const loose = [];
  for (const w of widgets || []) {
    if (w.tab_id && known.has(w.tab_id)) {
      if (w.tab_id === tabId) onTab.push(w);
    } else if (home && home.id === tabId) {
      loose.push(w);
    }
  }
  return [...onTab, ...loose];
}

// Server layouts ({ lg: { x, y, w, h }, ... } on each widget) to the shape
// buildLayouts reads ({ lg: [{ i, x, y, w, h }, ...] }). Widgets without a layout are
// left out, so buildLayouts places them like a new widget: in the first gap that fits,
// else at the bottom. A moved widget lands there too, without moving the
// tab's existing cards.
export function savedLayoutsFromWidgets(widgets) {
  const out = {};
  for (const bp of Object.keys(COLS)) out[bp] = [];
  for (const w of widgets || []) {
    for (const bp of Object.keys(COLS)) {
      const cell = w.layout?.[bp];
      if (cell) out[bp].push({ i: w.id, x: cell.x, y: cell.y, w: cell.w, h: cell.h, minW: 2, minH: 2 });
    }
  }
  return out;
}

// react-grid-layout's allLayouts to the request body for one tab: only the widgets in
// `widgetIds` (the user's own, on that tab), per breakpoint, as plain cells.
export function layoutPayload(allLayouts, widgetIds) {
  const wanted = new Set(widgetIds || []);
  const out = {};
  for (const bp of Object.keys(COLS)) {
    for (const item of allLayouts?.[bp] || []) {
      if (!wanted.has(item.i)) continue;
      if (!out[item.i]) out[item.i] = {};
      out[item.i][bp] = { x: item.x, y: item.y, w: item.w, h: item.h };
    }
  }
  return out;
}

function canonical(value) {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === 'object') {
    return Object.keys(value).sort().reduce((acc, k) => ({ ...acc, [k]: canonical(value[k]) }), {});
  }
  return value;
}

// Whether two payloads place every widget identically, whatever order their keys are
// in. The grid reports a layout change on mount and on every render that settles, and
// saving an unchanged layout each time would be a request per render. Key order is
// ignored because the server's copy comes back from JSONB, which reorders keys.
export function sameLayout(a, b) {
  return JSON.stringify(canonical(a || {})) === JSON.stringify(canonical(b || {}));
}

// Where each widget drawn on a tab sits for THIS user, as { [widgetId]: layout }.
//
// Your own widget on your own tab: its layout, which is also what viewers start from.
// Anyone else's widget: your own size for it on this tab if you have set one, else
// its owner's layout when it belongs to this tab, else nothing (appended). A viewer's
// resize of a shared tab therefore changes only their view.
export function effectiveLayouts(shownWidgets, tab) {
  const out = {};
  if (!tab) return out;
  const overrides = tab.layout_overrides || {};
  for (const w of shownWidgets || []) {
    const mine = !w.is_shared;
    const ownerLayout = w.tab_id === tab.id ? w.layout : null;
    const layout = mine ? ownerLayout : overrides[w.id] || ownerLayout;
    if (layout) out[w.id] = layout;
  }
  return out;
}

// The one-time move of a browser-saved layout to the server. Returns
// { [tabId]: payload } for widgets that have no server position yet but do have one
// in the old browser layout, grouped by the tab the request goes to:
//
// - your own widgets, to the tab they are on (stored on the widget);
// - colleagues' widgets with no tab you can see -- the ones the single-page
//   dashboard let you arrange too -- to your first own tab, where the server keeps
//   them as your own positions. Leaving them out would re-stack them on the switch.
export function importFromBrowserLayout(browserLayouts, widgets, tabs) {
  const known = new Set((tabs || []).map((t) => t.id));
  const home = homeTab(tabs);
  const homeOverrides = home?.layout_overrides || {};
  const byTab = {};
  for (const w of widgets || []) {
    let tabId;
    if (!w.is_shared) {
      if (w.layout || !w.tab_id) continue;
      tabId = w.tab_id;
    } else {
      if ((w.tab_id && known.has(w.tab_id)) || !home || homeOverrides[w.id]) continue;
      tabId = home.id;
    }
    const cells = layoutPayload(browserLayouts, [w.id])[w.id];
    if (!cells) continue;
    if (!byTab[tabId]) byTab[tabId] = {};
    byTab[tabId][w.id] = cells;
  }
  return byTab;
}

// A name for a tab made with "New tab": "New tab", then "New tab 2", ... -- whichever
// is free among the user's own tabs, ignoring case, matching the server's rule.
export function nextTabName(tabs, base = 'New tab') {
  const taken = new Set(ownTabs(tabs).map((t) => t.name.toLowerCase()));
  if (!taken.has(base.toLowerCase())) return base;
  for (let n = 2; n < 1000; n += 1) {
    const candidate = `${base} ${n}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
  return base;
}

// Client-side check of a name, so the common mistakes are caught before a round trip.
// The server checks again and decides; this only returns the sentence to show.
export function tabNameProblem(name, tabs, tabId) {
  const trimmed = (name || '').trim();
  if (!trimmed) return 'A tab needs a name.';
  if (trimmed.length > MAX_TAB_NAME_LENGTH) return `Tab names can be at most ${MAX_TAB_NAME_LENGTH} characters.`;
  const clash = ownTabs(tabs).some((t) => t.id !== tabId && t.name.toLowerCase() === trimmed.toLowerCase());
  if (clash) return 'You already have a tab with that name.';
  return null;
}

// The tab a delete sends its widgets to: the first remaining own tab, in the user's
// order. Must agree with the server, which applies the same rule.
export function deleteDestination(tabs, tabId) {
  return ownTabs(tabs).find((t) => t.id !== tabId) || null;
}

// The ids of the whole bar after moving one tab to a new index. Hidden tabs keep their
// place relative to the rest, so restoring one later does not shuffle the bar.
export function reorderedIds(tabs, fromId, toIndexInVisible) {
  const shown = visibleTabs(tabs);
  const moving = shown.find((t) => t.id === fromId);
  if (!moving) return (tabs || []).map((t) => t.id);
  const rest = shown.filter((t) => t.id !== fromId);
  const clamped = Math.max(0, Math.min(toIndexInVisible, rest.length));
  const newShown = [...rest.slice(0, clamped), moving, ...rest.slice(clamped)];
  const out = [];
  let k = 0;
  for (const t of tabs || []) {
    out.push(t.hidden ? t.id : newShown[k++].id);
  }
  return out;
}

// Apply an id order to the tab objects (for the optimistic update after a drag).
export function orderTabs(tabs, ids) {
  const byId = new Map((tabs || []).map((t) => [t.id, t]));
  return ids.map((id) => byId.get(id)).filter(Boolean);
}
